import { CSSResultGroup, html, LitElement } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { definitionListItemStyle, listStyle } from './definition-list.style.js'
import { hasNamedSlot } from 'elements/_shared/hasSlot.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'


initializeIconsByRole({
  'cse-definition-list-item.info': { name: 'circle-info', style: 'regular' },
})

@customElement('cse-definition-list-item')
export class CSEDefinitionListItem extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, fontStyles, definitionListItemStyle]

  @property({ type: String, attribute: 'info-icon-position' })
  infoIconPosition: 'prefix' | 'suffix' = 'prefix'

  private renderInfoIcon = () => {
    if (hasNamedSlot(this, 'info')) {
      return html`
        <cse-tooltip slot="${this.infoIconPosition}">
          <cse-icon
            slot="anchor"
            id="anchor"
            class="icon"
            icon-role="cse-definition-list-item.info"
          ></cse-icon>
          <slot name="info"></slot>
        </cse-tooltip>
      `
    }

    return html`
      <cse-icon class="hidden-icon" icon-role="cse-definition-list-item.info" slot="${this.infoIconPosition}"></cse-icon>
    `
  }

  private renderDescriptionInfoIcon = () => {
    if (hasNamedSlot(this, 'description-info')) {
      return html`
        <cse-tooltip slot="${this.infoIconPosition}">
          <cse-icon
            slot="anchor"
            class="icon"
            icon-role="cse-definition-list-item.info"
          ></cse-icon>
          <slot name="description-info"></slot>
        </cse-tooltip>
      `
    }

    return html`
      <cse-icon class="hidden-icon" icon-role="cse-definition-list-item.info" slot="${this.infoIconPosition}"></cse-icon>
    `
  }

  override render() {
    return html`
      <div class="cse-definition-list-item">
        <div class="term">
          <cse-text has-icon>
            ${this.renderInfoIcon()}
            <slot></slot>
          </cse-text>
        </div>
        <div class="definition">
          <cse-text has-icon>
            ${this.renderDescriptionInfoIcon()}
            <slot name="description"></slot>
          </cse-text>
        </div>
      </div>
    `
  }
}

@customElement('cse-definition-list')
export class CSEDefinitionList extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, listStyle]

  @query('slot')
  private defaultSlot!: HTMLSlotElement

  private handleSlotChange() {
    const assignedNodes = this.defaultSlot.assignedNodes({ flatten: true })
    const assignedDefinitions = assignedNodes.map(this.findDefinition).filter(this.isDefinition)

    assignedDefinitions.forEach((definition: CSEDefinitionListItem, index: number) => {
      const isOdd = index % 2 === 0

      index===0 ? definition.dataset['definitionFirst'] = '' : delete definition.dataset['definitionFirst']

      if (isOdd) {
        delete definition.dataset['definitionEven']
        definition.dataset['definitionOdd'] = ''
      } else {
        delete definition.dataset['definitionOdd']
        definition.dataset['definitionEven'] = ''
      }
    })
  }

  private findDefinition(node: Node): CSEDefinitionListItem | null {
    const selector = 'cse-definition-list-item'
    return node instanceof HTMLElement ? node.closest(selector) ?? node.querySelector(selector) : null
  }

  private isDefinition(definition: CSEDefinitionListItem | null): definition is CSEDefinitionListItem {
    return definition !== null
  }

  override render() {
    return html`
      <div class="cse-definition-list">
        <slot @slotchange="${this.handleSlotChange}"></slot>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-definition-list': CSEDefinitionList
    'cse-definition-list-item': CSEDefinitionListItem
  }
}
